import React, { useEffect, useState } from 'react';
import { get } from '../utils/apiClient';
import { getCachedData, setCachedData, isCacheStale } from '../utils/cacheUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';
import Base64Image from '../components/Base64Image';
import {
  BuildingProjectPageContainer,
  Section,
  SectionTitle,
  TextBlock,
  TwoColumn,
  ImageBlock,
  FloodGalleryGrid,
  FloodCard,
  FloodCaption,
  FloodDescription,
  StatusRow,
  ProgressBarWrapper,
  ProgressBarFill,
} from '../styles/BuildingProjectPageStyles';

const CACHE_KEY = 'building_project_data';
const MAX_AGE_MS = 1000 * 60 * 60;

const BuildingProjectPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const cached = await getCachedData(CACHE_KEY);

      if (cached && !isCacheStale(cached, MAX_AGE_MS)) {
        if (!isMounted) return;
        setPageData(cached.data);
        setLoading(false);
      }

      try {
        const data = await get('/building-project');
        if (!isMounted) return;
        setPageData(data);
        setCachedData(CACHE_KEY, data);
        setLoading(false);
      } catch (err) {
        if (!isMounted) return;
        if (!cached) {
          setLoading(false);
        }
        setError(err && err.message ? err.message : 'Failed to load building project data.');
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading && !pageData) {
    return (
      <BuildingProjectPageContainer>
        <LoadingSpinner />
      </BuildingProjectPageContainer>
    );
  }

  if (error && !pageData) {
    return <BuildingProjectPageContainer>{error}</BuildingProjectPageContainer>;
  }

  const header = pageData && pageData.header ? pageData.header : null;
  const projectOverview = pageData ? pageData.projectOverview : null;
  const reasonAndImpact = pageData ? pageData.reasonAndImpact : null;
  const floodGallery = pageData && Array.isArray(pageData.floodGallery) ? pageData.floodGallery : [];
  const donationCallToAction = pageData ? pageData.donationCallToAction : null;
  const projectStatus = pageData ? pageData.projectStatus : null;
  const biblicalReference = pageData ? pageData.biblicalReference : null;

  const progressPercent =
    projectStatus && typeof projectStatus.progressPercentage === 'number'
      ? Math.min(Math.max(projectStatus.progressPercentage, 0), 100)
      : null;

  return (
    <BuildingProjectPageContainer>
      {header && (
        <PageHeader
          title={header.title}
          image={header.backgroundImage}
        />
      )}

      {projectOverview && (
        <Section>
          <SectionTitle>Project Overview</SectionTitle>
          <TextBlock>
            {projectOverview.goal && (
              <p><strong>Goal:</strong> {projectOverview.goal}</p>
            )}
            {projectOverview.description && (
              <p>{projectOverview.description}</p>
            )}
            {typeof projectOverview.estimatedBudget === 'number' && (
              <p>
                <strong>Estimated Budget:</strong> ${projectOverview.estimatedBudget.toLocaleString()}
              </p>
            )}
            {projectOverview.timeline && (
              <p><strong>Timeline:</strong> {projectOverview.timeline}</p>
            )}
          </TextBlock>
        </Section>
      )}

      {reasonAndImpact && (reasonAndImpact.text || reasonAndImpact.image) && (
        <Section>
          <SectionTitle>Reason & Impact</SectionTitle>
          <TwoColumn>
            {reasonAndImpact.text && (
              <TextBlock>{reasonAndImpact.text}</TextBlock>
            )}
            {reasonAndImpact.image && (
              <ImageBlock>
                <Base64Image
                  image={reasonAndImpact.image}
                  alt="Reason and impact"
                  style={{ width: '100%', maxWidth: 420, borderRadius: 8 }}
                />
              </ImageBlock>
            )}
          </TwoColumn>
        </Section>
      )}

      {floodGallery.length > 0 && (
        <Section>
          <SectionTitle>Flood Documentation Gallery</SectionTitle>
          <FloodGalleryGrid>
            {floodGallery.map((item, index) => (
              <FloodCard key={index}>
                {item.image && (
                  <Base64Image
                    image={item.image}
                    alt={item.caption || 'Flood image'}
                    style={{ width: '100%', borderRadius: 6, marginBottom: 8 }}
                  />
                )}
                {item.caption && <FloodCaption>{item.caption}</FloodCaption>}
                {item.description && (
                  <FloodDescription>{item.description}</FloodDescription>
                )}
              </FloodCard>
            ))}
          </FloodGalleryGrid>
        </Section>
      )}

      {donationCallToAction && (donationCallToAction.title || donationCallToAction.message) && (
        <Section>
          <SectionTitle>
            {donationCallToAction.title || 'Support the Building Project'}
          </SectionTitle>
          <TextBlock>
            {donationCallToAction.message && <p>{donationCallToAction.message}</p>}
          </TextBlock>
        </Section>
      )}

      {projectStatus && (projectStatus.statusText || progressPercent !== null) && (
        <Section>
          <SectionTitle>Project Status</SectionTitle>
          <StatusRow>
            {projectStatus.statusText && <p>{projectStatus.statusText}</p>}
            {progressPercent !== null && (
              <ProgressBarWrapper>
                <ProgressBarFill style={{ width: `${progressPercent}%` }}>
                  {progressPercent}%
                </ProgressBarFill>
              </ProgressBarWrapper>
            )}
          </StatusRow>
        </Section>
      )}

      {biblicalReference && (biblicalReference.reference || biblicalReference.text) && (
        <Section>
          <SectionTitle>Biblical Reference</SectionTitle>
          <TextBlock>
            {biblicalReference.reference && (
              <p><strong>{biblicalReference.reference}</strong></p>
            )}
            {biblicalReference.text && <p>{biblicalReference.text}</p>}
          </TextBlock>
        </Section>
      )}
    </BuildingProjectPageContainer>
  );
};

export default BuildingProjectPage;
